// @ts-strict-ignore
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@node-space/ui-components'
import { isCryptoCustomerWallet } from 'pages/Customers/helpers/utils'
import { Customer, CustomerStatus } from 'types/customers'
import { SourceWallet } from 'types/move/move'
import { TransferWalletsReponse, WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import { formatString, roundNumberWithCommas } from 'utils/utils'

interface UseMoveFormOptionsProps {
  customerList: Customer[]
  transferWallets: TransferWalletsReponse[]
  sourceWallet: SourceWallet | null
  customerHasNoModel: boolean
  isEmbeddedOrRelianceCustomerWallet: boolean
  isCustomerWalletTransferType: boolean
}

export const useMoveFormOptions = ({
  customerList,
  sourceWallet,
  transferWallets,
  isEmbeddedOrRelianceCustomerWallet,
  customerHasNoModel,
  isCustomerWalletTransferType,
}: UseMoveFormOptionsProps) => {
  const { t } = useTranslation()

  const customerOptions = useMemo(() => {
    return customerList
      ?.filter?.(customer => customer?.status === CustomerStatus?.VERIFIED)
      ?.map?.(customer => ({
        value: customer?.reference,
        leftText: customer?.name,
      }))
  }, [customerList])

  const transferWalletOptions = useMemo(() => {
    if (!transferWallets) return []

    return transferWallets.map(wallet => {
      const code = wallet?.balance?.currency?.toLowerCase()
      const approximateBalance = roundNumberWithCommas(wallet?.balance?.value, 2)

      return {
        LeftElement: <Avatar name={code} size="small" />,
        leftText: wallet?.name,
        rightText: `${approximateBalance} ${code?.toUpperCase()}`,
        value: wallet?.walletId,
      }
    })
  }, [transferWallets])

  const selectedSourceWalletOption = useMemo(() => {
    if (!sourceWallet) return []

    return [
      {
        LeftElement: <Avatar name={sourceWallet.currency?.toLowerCase()} size="small" />,
        leftText: sourceWallet.walletName,
        rightText: formatString(
          t('balanceWithCurrency'),
          roundNumberWithCommas(sourceWallet.balance, 2),
          sourceWallet.currency
        ),
        value: sourceWallet.walletId,
      },
    ]
  }, [sourceWallet, t])

  const walletTransferTypeOptions = useMemo(() => {
    const isCryptoWallet = isCryptoCustomerWallet(sourceWallet.currency)

    return [
      {
        label: t('customers.toAnotherWallet'),
        value: WalletTransferType.MAIN,
      },
      ...((isEmbeddedOrRelianceCustomerWallet && isCryptoWallet) || customerHasNoModel
        ? []
        : [
            {
              label: t('customers.toACustomerWallet'),
              value: WalletTransferType.CUSTOMER,
            },
          ]),
    ]
  }, [t, isEmbeddedOrRelianceCustomerWallet, sourceWallet])

  // Labels
  const selectBeneficiaryDropdownPlaceholder = useMemo(() => {
    if (!!transferWalletOptions && !transferWalletOptions?.length) {
      return t('noWallets')
    } else {
      return t('select')
    }
  }, [transferWalletOptions, t])

  const destinationWalletLabel = useMemo(() => {
    if (isCustomerWalletTransferType) {
      return t('customers.selectCustomerWallet')
    }

    return t('customers.toWallet')
  }, [isCustomerWalletTransferType, t])

  return {
    customerOptions,
    transferWalletOptions,
    walletTransferTypeOptions,
    selectedSourceWalletOption,
    selectBeneficiaryDropdownPlaceholder,
    destinationWalletLabel,
  }
}
